import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import authApi from '../../api/auth';

const ResetPasswordCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">{title}</h2>
      {children}
    </div>
  );
};

const ResetPasswordForm = () => {
  const location = useLocation();
  const [step, setStep] = useState('request'); // 'request', 'verify', 'reset'
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAdminGenerated, setIsAdminGenerated] = useState(false);

  // Check for token in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenFromUrl = urlParams.get('token');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      setIsAdminGenerated(true);
      // Verify token and go directly to password reset form
      verifyTokenAndProceed(tokenFromUrl);
    }
  }, [location]);

  const verifyTokenAndProceed = async (tokenToVerify) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await authApi.verifyResetToken(tokenToVerify);
      setSuccess('');
      setStep('reset');
    } catch (error) {
      setError('Invalid or expired reset link. Please request a new one.');
      setStep('request');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await authApi.requestPasswordReset(email);
      setSuccess('Password reset instructions have been sent to your email.');
      
      // For development/demo purposes only:
      // In a real app, we would not expose the token in the UI
      if (response.token) {
        setToken(response.token);
        setSuccess('A reset link has been sent to your email. For this demo, you can use the token: ' + response.token);
        setStep('verify');
      }
    } catch (error) {
      setError('Failed to request password reset: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await authApi.verifyResetToken(token);
      setSuccess('Token verified successfully.');
      setStep('reset');
    } catch (error) {
      setError('Invalid or expired token: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await authApi.resetPassword(token, newPassword);
      setSuccess('Password has been reset successfully. You can now login with your new password.');
      setStep('complete');
    } catch (error) {
      setError('Failed to reset password: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while verifying token from URL
  if (loading && isAdminGenerated && step !== 'reset') {
    return (
      <div className="w-full max-w-md mx-auto space-y-6">
        <div className="flex items-center justify-center">
          <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Reset Password</h1>
        </div>
        <ResetPasswordCard title="Verifying Reset Link">
          <div className="flex justify-center py-4">
            <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-center text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
            Verifying your reset link...
          </p>
        </ResetPasswordCard>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto space-y-6">
      <div className="flex items-center justify-center">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Reset Password</h1>
      </div>
      
      {step === 'request' && (
        <ResetPasswordCard title="Request Password Reset">
          <form onSubmit={handleRequestSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block mb-1 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                className="w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 transition-colors"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            {error && (
              <div className="p-3 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
                {error}
              </div>
            )}
            
            {success && (
              <div className="p-3 text-sm text-status-success-light-text dark:text-status-success-dark-text bg-status-success-light-bg dark:bg-status-success-dark-bg rounded-md">
                {success}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2 text-white dark:text-ui-dark-text-primary animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : 'Request Password Reset'}
            </button>
            
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                Back to Login
              </Link>
            </div>
          </form>
        </ResetPasswordCard>
      )}
      
      {step === 'verify' && (
        <ResetPasswordCard title="Verify Reset Token">
          <form onSubmit={handleVerifySubmit} className="space-y-4">
            <div>
              <label htmlFor="token" className="block mb-1 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                Reset Token
              </label>
              <input
                type="text"
                id="token"
                className="w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 transition-colors"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
              />
              <p className="mt-1 text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                Enter the token that was sent to your email.
              </p>
            </div>
            
            {error && (
              <div className="p-3 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
                {error}
              </div>
            )}
            
            {success && (
              <div className="p-3 text-sm text-status-success-light-text dark:text-status-success-dark-text bg-status-success-light-bg dark:bg-status-success-dark-bg rounded-md">
                {success}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2 text-white dark:text-ui-dark-text-primary animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </div>
              ) : 'Verify Token'}
            </button>
            
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                Back to Login
              </Link>
            </div>
          </form>
        </ResetPasswordCard>
      )}
      
      {step === 'reset' && (
        <ResetPasswordCard title={isAdminGenerated ? "Set Your Password" : "Set New Password"}>
          {isAdminGenerated && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                An administrator has created this password reset link for you. Please set your new password below.
              </p>
            </div>
          )}
          <form onSubmit={handleResetSubmit} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block mb-1 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                className="w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 transition-colors"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength="8"
                required
              />
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block mb-1 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="w-full p-2 border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 transition-colors"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            {error && (
              <div className="p-3 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
                {error}
              </div>
            )}
            
            {success && (
              <div className="p-3 text-sm text-status-success-light-text dark:text-status-success-dark-text bg-status-success-light-bg dark:bg-status-success-dark-bg rounded-md">
                {success}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2 text-white dark:text-ui-dark-text-primary animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Resetting...
                </div>
              ) : 'Reset Password'}
            </button>
            
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                Back to Login
              </Link>
            </div>
          </form>
        </ResetPasswordCard>
      )}
      
      {step === 'complete' && (
        <ResetPasswordCard title="Password Reset Complete" className="bg-gradient-to-r from-status-success-light-bg to-status-success-light-bg/80 dark:from-status-success-dark-bg/30 dark:to-status-success-dark-bg/30 border border-status-success-light-bg dark:border-status-success-dark-bg/50">
          <div className="text-center space-y-4">
            <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
              {success}
            </p>
            <Link
              to="/login"
              className="inline-block px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
            >
              Go to Login
            </Link>
          </div>
        </ResetPasswordCard>
      )}
    </div>
  );
};

export default ResetPasswordForm;